import * as React from 'react';

import PackActionModals, {
  ARCHIVE,
  CLOSE,
  EDIT_ACTIVITIES,
  RENAME,
  REOPEN,
  SHARE,
} from './packActionModals';
import {
  ALL,
  Pack,
  escapeHtml,
  manageStudentsLink,
} from './shared';

import { ActionMenu, EXTRA_SMALL_ICON_BASE_SRC, Tooltip } from '../../../../Shared/index';

const PACK_MENU_OPENED_EVENT = 'manageActivitiesV2:packMenuOpened'

interface PackActionsProps {
  pack: Pack;
  selectedClassroomId: string | number;
  onPackChanged: (snackbarCopy?: string) => void;
  isBoosted?: boolean;
  onToggleBoost?: () => void;
  isDiagnostic?: boolean;
  companionPack?: Pack;
  // pass both to open these modals from the card's own buttons
  showModal?: string;
  setShowModal?: React.Dispatch<React.SetStateAction<string>>;
}


const PackActions = ({ pack, selectedClassroomId, onPackChanged, isBoosted, onToggleBoost, isDiagnostic, companionPack, showModal: controlledShowModal, setShowModal: controlledSetShowModal }: PackActionsProps) => {
  const [ownShowModal, setOwnShowModal] = React.useState('')
  const showModal = controlledSetShowModal ? controlledShowModal : ownShowModal
  const setShowModal = controlledSetShowModal || setOwnShowModal
  const [menuOpen, setMenuOpen] = React.useState(false)
  const menuId = React.useId()
  const [activityToShare, setActivityToShare] = React.useState(null)

  function openPackShareModal() {
    setActivityToShare(null)
    setShowModal(SHARE)
  }

  function toggleMenu() {
    const nextOpen = !menuOpen
    if (nextOpen) { document.dispatchEvent(new CustomEvent(PACK_MENU_OPENED_EVENT, { detail: menuId })) }
    setMenuOpen(nextOpen)
  }

  React.useEffect(() => {
    if (!menuOpen) { return }
    function handleOtherMenuOpened(e: Event) {
      if ((e as CustomEvent).detail !== menuId) { setMenuOpen(false) }
    }
    document.addEventListener(PACK_MENU_OPENED_EVENT, handleOtherMenuOpened)
    return () => document.removeEventListener(PACK_MENU_OPENED_EVENT, handleOtherMenuOpened)
  }, [menuOpen, menuId])

  function suppressTriggerMouseDown(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  const isOpen = pack.open !== false

  const isOwner = Boolean(pack.ownedByCurrentUser)
  const coTeacherReason = `Only ${pack.ownerName || 'the teacher who created this pack'} can do this. As a co-teacher you can still boost and share this pack.`
  const coTeacherReasonHtml = escapeHtml(coTeacherReason)

  const disabledAction = (name: string) => ({
    element: (
      <button aria-disabled="true" aria-label={`${name}. ${coTeacherReason}`} className="action action--disabled focus-on-light" role="menuitem" type="button">
        <Tooltip isTabbable={false} tooltipText={coTeacherReasonHtml} tooltipTriggerText={name} />
      </button>
    ),
  })

  const shareAction = isOpen ? [{ name: 'Share', action: openPackShareModal }] : []

  const menuActions = isOwner
    ? [
      ...(isOpen ? [
        { name: 'Edit dates & activities', action: () => setShowModal(EDIT_ACTIVITIES) },
        { name: 'Edit students', action: () => { window.location.href = manageStudentsLink(pack.unitId) } },
        ...shareAction,
      ] : []),
      { name: 'Rename', action: () => setShowModal(RENAME) },
      isOpen
        ? { name: 'Close', action: () => setShowModal(CLOSE) }
        : { name: 'Reopen', action: () => setShowModal(REOPEN) },
      { name: 'Delete', action: () => setShowModal(ARCHIVE) },
    ]
    : [
      ...(isOpen ? [
        disabledAction('Edit dates & activities'),
        disabledAction('Edit students'),
        ...shareAction,
      ] : []),
      disabledAction('Rename'),
      disabledAction(isOpen ? 'Close' : 'Reopen'),
      disabledAction('Delete'),
    ]

  const ellipsisIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/ellipsis-borderless.svg`

  const selectedClassroom = selectedClassroomId === ALL ? null : pack.classrooms.find(c => String(c.id) === String(selectedClassroomId))

  const boostDisabled = Boolean(selectedClassroom?.staggered)
  const boostTooltip = boostDisabled
    ? 'This pack is a recommendation from a diagnostic that has staggered release turned on.<br/><br/>Staggered release makes students complete the recommended packs one at a time, so this one can\'t be boosted.<br/><br/>To boost it, turn off staggered release for that diagnostic in this class.'
    : (isBoosted
      ? 'Boosted for this class. It floats to the top of your students\' to-do lists. Click to remove.'
      : 'Boost this pack to float it to the top of your students\' to-do lists, just for this class.')

  const boostLabel = isBoosted ? 'Boosted' : 'Boost'

  const boostButton = onToggleBoost && isOpen ? (
    <button
      aria-disabled={boostDisabled ? 'true' : undefined}
      aria-label={`Boost ${pack.unitName}`}
      aria-pressed={Boolean(isBoosted)}
      className={`quill-button extra-small outlined grey boost-button focus-on-light ${isBoosted ? 'boosted' : ''}${boostDisabled ? ' boost-button--disabled' : ''}`}
      onClick={boostDisabled ? undefined : onToggleBoost}
      type="button"
    >
      <img alt="" height="16" src={`${EXTRA_SMALL_ICON_BASE_SRC}/bolt.svg`} width="16" />
      <span className="boost-label">{boostLabel}</span>
    </button>
  ) : null

  return (
    <div className="pack-card-icons">
      {boostButton && (menuOpen ? boostButton : (
        <Tooltip
          isTabbable={false}
          tooltipText={boostTooltip}
          tooltipTriggerText={boostButton}
        />
      ))}
      <div className="actions-menu-container">
        <ActionMenu
          actions={menuActions}
          buttonAriaLabel={`Actions for ${pack.unitName}`}
          isOpen={menuOpen}
          onToggle={toggleMenu}
          triggerElement={
            <button
              className="quill-button outlined extra-small grey actions-button focus-on-light"
              onMouseDown={suppressTriggerMouseDown}
              type="button"
            >
              <img alt="" src={ellipsisIconSrc} />
            </button>
          }
        />
      </div>
      <PackActionModals
        activityToShare={activityToShare}
        companionPack={companionPack}
        isDiagnostic={isDiagnostic}
        onPackChanged={onPackChanged}
        pack={pack}
        selectedClassroomId={selectedClassroomId}
        setActivityToShare={setActivityToShare}
        setShowModal={setShowModal}
        showModal={showModal}
      />
    </div>
  )
}

export default PackActions
